import api from "@/services/api";
import { assertUnreachable, QueryFunctionContext } from "@/utils/typing";
import { IconName } from "@/services/icons";
import type { OmniumCoverageRecord } from "@/services/crm";
import {
  computed,
  DeepReadonly,
  reactive,
  Ref,
  toValue,
  type MaybeRefOrGetter,
} from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import {
  useManageResourcesTracking,
  useManageResourceGroupsTracking,
} from "@/services/gtm";
import { circumventAdBlocker } from "@/services/utils";
import {
  PublicDashboardFilters,
  usePublicDashboardQueryFilters,
} from "@/services/public_dashboard";

const gtmResource = useManageResourcesTracking();
const gtmResourceGroup = useManageResourceGroupsTracking();

export enum ResourceChannel {
  // These MUST reflect the Channel enum in the backend, AT ALL TIMES
  // See backend/resources/enums.py
  // If you need to display the channel name to a user, use channelDetails[ResourceChannel.<channel>].humanName
  FACEBOOK_ADS = "FacebookAds",
  FACEBOOK_ORGANIC = "FacebookOrganic",
  INSTAGRAM_ORGANIC = "InstagramOrganic",
  GOOGLE_SHEETS = "GoogleSheets",
  GOOGLE_ADS = "GoogleAds",
  GOOGLE_ANALYTICS = "GoogleAnalytics",
  GOOGLE_ANALYTICS_4 = "GoogleAnalytics4",
  MATOMO = "Matomo",
  GOOGLE_BIG_QUERY = "GoogleBigQuery",
  GOOGLE_BIG_QUERY_ANALYTICS = "GoogleBigQueryAnalytics",
  OMNIUM = "Omnium",
  SHOPIFY = "Shopify",
  DV_360 = "DV360",
  CM_360 = "CM360",
  MERCHANT_CENTER = "MerchantCenter",
  GOOGLE_SEARCH_CONSOLE = "GoogleSearchConsole",
  YOUTUBE = "Youtube",
  LINKED_IN_ADS = "LinkedInAds",
  LINKED_IN_ORGANIC = "LinkedInOrganic",
  SNAPCHAT_ADS = "SnapchatAds",
  XANDR = "Xandr",
  BING_ADS = "BingAds",
  C_ALT = "CAlt",
  TIK_TOK_ADS = "TikTokAds",
  TIK_TOK_ORGANIC = "TikTokOrganic",
  KOBLER = "Kobler",
  ADFORM = "Adform",
}

export const disabledChannels: ResourceChannel[] = [
  ResourceChannel.GOOGLE_BIG_QUERY_ANALYTICS,
];

export const hiddenChannels: ResourceChannel[] = [...disabledChannels];

export const restrictedChannels: ResourceChannel[] = [...hiddenChannels];

export const CRM_CHANNELS: ResourceChannel[] = [
  ResourceChannel.OMNIUM,
  ResourceChannel.SHOPIFY,
];

// Only these write loyalty (member) facts; see backend CRM_LOYALTY_CHANNELS.
export const CRM_LOYALTY_CHANNELS: ResourceChannel[] = [ResourceChannel.OMNIUM];

// Grouping used by the "Connect a channel" grid.
export enum ChannelCategory {
  ADVERTISING = "Advertising",
  ANALYTICS = "Analytics",
  SOCIAL_MEDIA = "Social media",
  DATA_ECOMMERCE = "Data & ecommerce",
}

export const channelCategoryOrder: ChannelCategory[] = [
  ChannelCategory.ADVERTISING,
  ChannelCategory.ANALYTICS,
  ChannelCategory.SOCIAL_MEDIA,
  ChannelCategory.DATA_ECOMMERCE,
];

// "key" channels collect credentials in their own form component instead of
// redirecting; "import" (Custom Data Import) picks a file rather than an account.
export type ChannelAuthType = "oauth" | "key" | "import";

export interface ChannelDetail {
  name: ResourceChannel;
  category: ChannelCategory;
  authType: ChannelAuthType;
  // The identity provider the user signs in with, when it differs from the
  // channel itself (Meta for Instagram, Google for GA4).
  platform?: string;
  backgroundColor: string;
  iconName: IconName;
  iconColor?: string;
  humanName: string;
  beta?: boolean;
  premium?: boolean;
}

export const channelDetails: Record<ResourceChannel, ChannelDetail> = {
  [ResourceChannel.FACEBOOK_ADS]: {
    name: ResourceChannel.FACEBOOK_ADS,
    category: ChannelCategory.ADVERTISING,
    authType: "oauth",
    platform: "Meta",
    backgroundColor: "bg-sky-600",
    iconName: "meta",
    iconColor: "text-facebook-blue",
    humanName: "Meta Ads",
  },
  [ResourceChannel.FACEBOOK_ORGANIC]: {
    name: ResourceChannel.FACEBOOK_ORGANIC,
    category: ChannelCategory.SOCIAL_MEDIA,
    authType: "oauth",
    platform: "Meta",
    backgroundColor: "bg-sky-600",
    iconName: "facebook",
    iconColor: "text-facebook-blue",
    humanName: "Facebook",
  },
  [ResourceChannel.INSTAGRAM_ORGANIC]: {
    name: ResourceChannel.INSTAGRAM_ORGANIC,
    category: ChannelCategory.SOCIAL_MEDIA,
    authType: "oauth",
    platform: "Meta",
    backgroundColor: "bg-pink-700",
    iconName: "instagram",
    iconColor: "text-pink-700",
    humanName: "Instagram",
  },
  [ResourceChannel.GOOGLE_ADS]: {
    name: ResourceChannel.GOOGLE_ADS,
    category: ChannelCategory.ADVERTISING,
    authType: "oauth",
    platform: "Google",
    backgroundColor: "bg-teal-600",
    iconName: "google-ads",
    humanName: "Google Ads",
  },
  [ResourceChannel.GOOGLE_ANALYTICS]: {
    name: ResourceChannel.GOOGLE_ANALYTICS,
    category: ChannelCategory.ANALYTICS,
    authType: "oauth",
    platform: "Google",
    backgroundColor: "bg-orange-500",
    iconName: "google-analytics",
    humanName: "Google Analytics",
  },
  [ResourceChannel.GOOGLE_ANALYTICS_4]: {
    name: ResourceChannel.GOOGLE_ANALYTICS_4,
    category: ChannelCategory.ANALYTICS,
    authType: "oauth",
    platform: "Google",
    backgroundColor: "bg-orange-500",
    iconName: "google-analytics",
    humanName: "Google Analytics 4",
  },
  [ResourceChannel.MATOMO]: {
    name: ResourceChannel.MATOMO,
    category: ChannelCategory.ANALYTICS,
    authType: "key",
    backgroundColor: "bg-blue-600",
    iconName: "matomo",
    humanName: "Matomo",
  },
  [ResourceChannel.GOOGLE_BIG_QUERY]: {
    name: ResourceChannel.GOOGLE_BIG_QUERY,
    category: ChannelCategory.ANALYTICS,
    authType: "oauth",
    platform: "Google",
    backgroundColor: "bg-blue-500",
    iconName: "google-analytics",
    humanName: "Google BigQuery",
  },
  [ResourceChannel.GOOGLE_BIG_QUERY_ANALYTICS]: {
    name: ResourceChannel.GOOGLE_BIG_QUERY_ANALYTICS,
    category: ChannelCategory.ANALYTICS,
    authType: "oauth",
    platform: "Google",
    backgroundColor: "bg-red-500",
    iconName: "google-analytics",
    humanName: "Google BigQuery Analytics",
  },
  [ResourceChannel.GOOGLE_SEARCH_CONSOLE]: {
    name: ResourceChannel.GOOGLE_SEARCH_CONSOLE,
    category: ChannelCategory.ANALYTICS,
    authType: "oauth",
    platform: "Google",
    backgroundColor: "bg-violet-500",
    iconName: "google-search-console",
    humanName: "Google Search Console",
  },
  [ResourceChannel.YOUTUBE]: {
    name: ResourceChannel.YOUTUBE,
    category: ChannelCategory.SOCIAL_MEDIA,
    authType: "oauth",
    platform: "Google",
    backgroundColor: "bg-red-600",
    iconName: "youtube",
    iconColor: "text-youtube-red",
    humanName: "YouTube",
  },
  [ResourceChannel.MERCHANT_CENTER]: {
    name: ResourceChannel.MERCHANT_CENTER,
    category: ChannelCategory.DATA_ECOMMERCE,
    authType: "oauth",
    platform: "Google",
    backgroundColor: "bg-blue-600",
    iconName: "google-merchant-center",
    iconColor: "text-blue-600",
    humanName: "Google Merchant Center",
  },
  [ResourceChannel.DV_360]: {
    name: ResourceChannel.DV_360,
    category: ChannelCategory.ADVERTISING,
    authType: "oauth",
    platform: "Google",
    backgroundColor: "bg-green-600",
    iconName: "google-dv360",
    iconColor: "text-green-600",
    humanName: "Google DV360",
    premium: true,
  },
  [ResourceChannel.CM_360]: {
    name: ResourceChannel.CM_360,
    category: ChannelCategory.ADVERTISING,
    authType: "oauth",
    platform: "Google",
    backgroundColor: "bg-lime-600",
    iconName: "google-cm360",
    iconColor: "text-lime-700",
    humanName: "Google CM360",
    beta: true,
    premium: true,
  },
  [ResourceChannel.LINKED_IN_ADS]: {
    name: ResourceChannel.LINKED_IN_ADS,
    category: ChannelCategory.ADVERTISING,
    authType: "oauth",
    platform: "LinkedIn",
    backgroundColor: "bg-linkedin-blue",
    iconName: "linked-in",
    iconColor: "text-linkedin-blue",
    humanName: "LinkedIn Ads",
  },
  // TODO: Differentiate it from LINKED_IN_ADS?
  [ResourceChannel.LINKED_IN_ORGANIC]: {
    name: ResourceChannel.LINKED_IN_ORGANIC,
    category: ChannelCategory.SOCIAL_MEDIA,
    authType: "oauth",
    platform: "LinkedIn",
    backgroundColor: "bg-blue-700",
    iconName: "linked-in",
    iconColor: "text-linkedin-blue",
    humanName: "LinkedIn",
  },
  [ResourceChannel.SNAPCHAT_ADS]: {
    name: ResourceChannel.SNAPCHAT_ADS,
    category: ChannelCategory.ADVERTISING,
    authType: "oauth",
    platform: "Snapchat",
    backgroundColor: "bg-amber-700",
    iconName: "snapchat-ghost",
    humanName: "Snapchat Ads",
  },
  [ResourceChannel.XANDR]: {
    name: ResourceChannel.XANDR,
    category: ChannelCategory.ADVERTISING,
    authType: "key",
    backgroundColor: "bg-xandr-red",
    iconName: "xandr",
    humanName: "Xandr",
    premium: true,
  },
  [ResourceChannel.BING_ADS]: {
    name: ResourceChannel.BING_ADS,
    category: ChannelCategory.ADVERTISING,
    authType: "oauth",
    platform: "Microsoft",
    backgroundColor: "bg-green-600",
    iconName: "microsoft",
    iconColor: "text-bing-teal",
    humanName: "Microsoft Ads",
  },
  [ResourceChannel.C_ALT]: {
    name: ResourceChannel.C_ALT,
    category: ChannelCategory.ADVERTISING,
    authType: "key",
    backgroundColor: "bg-cyan-700",
    iconName: "circle-dot",
    iconColor: "text-cyan-700",
    humanName: "C·ALT",
  },
  [ResourceChannel.TIK_TOK_ADS]: {
    name: ResourceChannel.TIK_TOK_ADS,
    category: ChannelCategory.ADVERTISING,
    authType: "oauth",
    platform: "TikTok",
    backgroundColor: "bg-pink-600",
    iconName: "tik-tok",
    iconColor: "text-black",
    humanName: "TikTok Ads",
  },
  [ResourceChannel.TIK_TOK_ORGANIC]: {
    name: ResourceChannel.TIK_TOK_ORGANIC,
    category: ChannelCategory.SOCIAL_MEDIA,
    authType: "oauth",
    platform: "TikTok",
    backgroundColor: "bg-pink-600",
    iconName: "tik-tok",
    iconColor: "text-black",
    humanName: "TikTok",
  },
  [ResourceChannel.KOBLER]: {
    name: ResourceChannel.KOBLER,
    category: ChannelCategory.ADVERTISING,
    authType: "key",
    backgroundColor: "bg-yellow-700",
    iconName: "kobler",
    humanName: "Kobler",
    premium: true,
  },
  [ResourceChannel.ADFORM]: {
    name: ResourceChannel.ADFORM,
    category: ChannelCategory.ADVERTISING,
    authType: "oauth",
    platform: "Adform",
    backgroundColor: "bg-blue-700",
    iconName: "adform",
    humanName: "Adform",
    premium: true,
  },
  [ResourceChannel.GOOGLE_SHEETS]: {
    name: ResourceChannel.GOOGLE_SHEETS,
    category: ChannelCategory.DATA_ECOMMERCE,
    authType: "import",
    platform: "Google",
    backgroundColor: "bg-green-700",
    iconName: "table-cells",
    iconColor: "text-purple-700",
    humanName: "Custom Data Import",
  },
  [ResourceChannel.OMNIUM]: {
    name: ResourceChannel.OMNIUM,
    category: ChannelCategory.DATA_ECOMMERCE,
    authType: "key",
    backgroundColor: "bg-emerald-700",
    iconName: "omnium",
    humanName: "Omnium",
    beta: true,
    premium: true,
  },
  [ResourceChannel.SHOPIFY]: {
    name: ResourceChannel.SHOPIFY,
    category: ChannelCategory.DATA_ECOMMERCE,
    authType: "oauth",
    platform: "Shopify",
    backgroundColor: "bg-green-700",
    iconName: "shopify",
    iconColor: "text-[#95BF47]",
    humanName: "Shopify",
    beta: true,
    premium: true,
  },
};

export interface BaseResource {
  createdAt: string;
  externalId: string;
  isInaccessible: boolean;
  isRemoved: boolean;
  modifiedAt: string;
  manuallyFetchedAt: string | null;
  canFetchManually: boolean;
  pk: number;
  token: number;
  user: number;
  name: string;
  iconUrl?: string | null;
}

export interface FacebookAttributionWindows {
  // Meta's action_attribution_windows: a resource is pinned to at most one token.
  // Kept as a list (Meta's param is a list); empty/absent means the unified
  // (per ad set) setting.
  actionAttributionWindows?: string[];
}

export interface FacebookAdsResource extends BaseResource {
  channel: ResourceChannel.FACEBOOK_ADS;
  metadata: { attributionWindows?: FacebookAttributionWindows };
}

export interface FacebookOrganicResource extends BaseResource {
  channel: ResourceChannel.FACEBOOK_ORGANIC;
  metadata: { profileImageUrl?: string };
}

export interface InstagramOrganicResource extends BaseResource {
  channel: ResourceChannel.INSTAGRAM_ORGANIC;
  metadata: { profileImageUrl?: string; username?: string };
}

export interface GoogleAdsResource extends BaseResource {
  channel: ResourceChannel.GOOGLE_ADS;
  metadata: Record<string, never>;
}

export interface GoogleAnalyticsResource extends BaseResource {
  channel: ResourceChannel.GOOGLE_ANALYTICS;
  metadata: { hasEcommerce?: boolean };
}

export interface GoogleAnalytics4Resource extends BaseResource {
  channel: ResourceChannel.GOOGLE_ANALYTICS_4;
  metadata: Record<string, never>;
}

export interface GoogleBigQueryResource extends BaseResource {
  channel: ResourceChannel.GOOGLE_BIG_QUERY;
  metadata: Record<string, never>;
}

export interface GoogleBigQueryAnalyticsResource extends BaseResource {
  channel: ResourceChannel.GOOGLE_BIG_QUERY_ANALYTICS;
  metadata: Record<string, never>;
}

export interface GoogleSearchConsoleResource extends BaseResource {
  channel: ResourceChannel.GOOGLE_SEARCH_CONSOLE;
  metadata: Record<string, never>;
}

export interface YoutubeResource extends BaseResource {
  channel: ResourceChannel.YOUTUBE;
  metadata: Record<string, never>;
}

export interface MerchantCenterResource extends BaseResource {
  channel: ResourceChannel.MERCHANT_CENTER;
  metadata: Record<string, never>;
}

export interface CM360Resource extends BaseResource {
  channel: ResourceChannel.CM_360;
  metadata: Record<string, never>;
}

export interface DV360Resource extends BaseResource {
  channel: ResourceChannel.DV_360;
  metadata: Record<string, never>;
}

export interface LinkedInAdsResource extends BaseResource {
  channel: ResourceChannel.LINKED_IN_ADS;
  metadata: Record<string, never>;
}

export interface LinkedInOrganicResource extends BaseResource {
  channel: ResourceChannel.LINKED_IN_ORGANIC;
  metadata: { profileImageUrl?: string };
}

export interface AttributionWindows {
  clickThrough?: string;
  viewThrough?: string;
}

export interface SnapchatAdsResource extends BaseResource {
  channel: ResourceChannel.SNAPCHAT_ADS;
  metadata: { attributionWindows?: AttributionWindows };
}
export interface XandrResource extends BaseResource {
  channel: ResourceChannel.XANDR;
  metadata: Record<string, never>;
}

export interface BingAdsResource extends BaseResource {
  channel: ResourceChannel.BING_ADS;
  metadata: Record<string, never>;
}

export interface CAltResource extends BaseResource {
  channel: ResourceChannel.C_ALT;
  metadata: Record<string, never>;
}

export interface TikTokAdsResource extends BaseResource {
  channel: ResourceChannel.TIK_TOK_ADS;
  metadata: Record<string, never>;
}

export interface TikTokOrganicResource extends BaseResource {
  channel: ResourceChannel.TIK_TOK_ORGANIC;
  metadata: Record<string, never>;
}

export interface KoblerResource extends BaseResource {
  channel: ResourceChannel.KOBLER;
  metadata: Record<string, never>;
}

export interface AdformResource extends BaseResource {
  channel: ResourceChannel.ADFORM;
  metadata: Record<string, never>;
}

export interface MatomoResource extends BaseResource {
  channel: ResourceChannel.MATOMO;
  metadata: {
    server_url?: string;
  };
}

export interface GoogleSheetsResource extends BaseResource {
  channel: ResourceChannel.GOOGLE_SHEETS;
  metadata: Record<string, never>;
}

export interface OmniumResource extends BaseResource {
  channel: ResourceChannel.OMNIUM;
  // Brand keys the account is limited to; absent or empty means every store.
  metadata: {
    brands?: string[];
    crmCoverage?: OmniumCoverageRecord;
    // A sibling of the record, not a field in it — see OmniumCoverageRecord.
    crmCoverageMuted?: boolean;
  };
}

export interface ShopifyResource extends BaseResource {
  channel: ResourceChannel.SHOPIFY;
  metadata: Record<string, never>;
}

export type Resource =
  | FacebookAdsResource
  | FacebookOrganicResource
  | InstagramOrganicResource
  | GoogleAdsResource
  | GoogleAnalyticsResource
  | GoogleAnalytics4Resource
  | GoogleBigQueryResource
  | GoogleBigQueryAnalyticsResource
  | GoogleSearchConsoleResource
  | YoutubeResource
  | MerchantCenterResource
  | DV360Resource
  | CM360Resource
  | LinkedInAdsResource
  | LinkedInOrganicResource
  | SnapchatAdsResource
  | XandrResource
  | BingAdsResource
  | CAltResource
  | TikTokAdsResource
  | TikTokOrganicResource
  | KoblerResource
  | AdformResource
  | MatomoResource
  | GoogleSheetsResource
  | OmniumResource
  | ShopifyResource;

export interface ResourceAccount {
  name: string;
  id: string;
  account?: string; // returned on Google Analytics
  property?: string; // returned on Google Analytics
  propertyId?: string; // returned on Google Analytics
}
interface CreateResourcePayload {
  channel: ResourceChannel;
  externalId: string;
  name: string;
  brands?: string[];
}

function createResource(payload: CreateResourcePayload): Promise<Resource> {
  return api
    .post("/resources/resources/", payload)
    .then((response) => response.data);
}

export function useCreateResourceMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: createResource,
      onError: () => gtmResource.saveCreate("Error"),
      onSuccess: (newResource) => {
        gtmResource.saveCreate("Success");
        queryClient.setQueryData<Resource[]>(["resources", {}], (resources) => [
          ...(resources ?? []),
          newResource,
        ]);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          predicate: (query) => {
            if (query.queryKey[0] === "resources") {
              return true;
            }
            // Also invalidate any other queries using a resource filter
            const params = query.queryKey?.[1];
            return (
              typeof params === "object" &&
              params !== null &&
              ("resource__in" in params || "not__resource__in" in params)
            );
          },
        });
      },
    }),
  );
}

function listResources({
  queryKey: [, params],
}: QueryFunctionContext<PublicDashboardFilters>): Promise<Resource[]> {
  const channels = new Set(Object.values(ResourceChannel));
  return api
    .get<Resource[]>("/resources/resources/", { params })
    .then((response) => {
      return response.data.filter((resource) => channels.has(resource.channel));
    });
}

export function useResourcesQuery(enabled: MaybeRefOrGetter<boolean> = true) {
  return reactive(
    useQuery({
      queryKey: ["resources", { ...usePublicDashboardQueryFilters() }] as const,
      queryFn: listResources,
      enabled,
    }),
  );
}

function deleteResource(resourceId: number): Promise<void> {
  return api.delete(`/resources/resources/${resourceId}/`);
}

export function useDeleteResourceMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: deleteResource,
      onError: () => gtmResource.saveDelete("Error"),
      onSuccess: (_, pk) => {
        gtmResource.saveDelete("Success");
        queryClient.setQueriesData<Resource[]>(
          { queryKey: ["resources"] },
          (resources) =>
            resources?.filter((resource: Resource) => resource.pk !== pk),
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKeys = ["resources", "resourceGroups"];
            if (queryKeys.includes(query.queryKey[0] as string)) {
              return true;
            }
            // Also invalidate any other queries using a resource filter
            const params = query.queryKey?.[1];
            return (
              typeof params === "object" &&
              params !== null &&
              ("resource__in" in params || "not__resource__in" in params)
            );
          },
        });
      },
    }),
  );
}

export const MANUAL_FETCH_LOOKBACK_DAY_OPTIONS = [1, 7, 14, 30, 90] as const;
export type ManualFetchLookbackDays =
  (typeof MANUAL_FETCH_LOOKBACK_DAY_OPTIONS)[number];

function fetchManually(payload: {
  resources: number[];
  competitorMonitoring: boolean;
  siteScore: boolean;
  lookbackDays?: ManualFetchLookbackDays;
}): Promise<void> {
  return api
    .post("/resources/fetch_manually/", payload)
    .then((response) => response.data);
}

export function useFetchManuallyMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: fetchManually,
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ["resources"],
        });
        queryClient.invalidateQueries({
          queryKey: ["competitors"],
        });
        queryClient.invalidateQueries({
          queryKey: ["organization"],
        });
      },
    }),
  );
}

export const channelToPath = {
  [ResourceChannel.FACEBOOK_ADS]: "facebook_ads",
  [ResourceChannel.FACEBOOK_ORGANIC]: "facebook_organic",
  [ResourceChannel.INSTAGRAM_ORGANIC]: "instagram_organic",
  [ResourceChannel.GOOGLE_ADS]: "google_ads",
  [ResourceChannel.GOOGLE_ANALYTICS]: "google_analytics",
  [ResourceChannel.GOOGLE_ANALYTICS_4]: "google_analytics_4",
  [ResourceChannel.GOOGLE_BIG_QUERY]: "google_big_query",
  [ResourceChannel.GOOGLE_BIG_QUERY_ANALYTICS]: "google_big_query_analytics",
  [ResourceChannel.GOOGLE_SEARCH_CONSOLE]: "google_search_console",
  [ResourceChannel.YOUTUBE]: "youtube",
  [ResourceChannel.MERCHANT_CENTER]: "merchant_center",
  [ResourceChannel.DV_360]: "dv_360",
  [ResourceChannel.CM_360]: "cm_360",
  [ResourceChannel.LINKED_IN_ADS]: "linked_in_ads",
  [ResourceChannel.LINKED_IN_ORGANIC]: "linked_in_organic",
  [ResourceChannel.SNAPCHAT_ADS]: "snapchat_ads",
  [ResourceChannel.XANDR]: "xandr",
  [ResourceChannel.BING_ADS]: "bing_ads",
  [ResourceChannel.C_ALT]: "c_alt",
  [ResourceChannel.TIK_TOK_ORGANIC]: "tik_tok_organic",
  [ResourceChannel.TIK_TOK_ADS]: "tik_tok_ads",
  [ResourceChannel.KOBLER]: "kobler",
  [ResourceChannel.ADFORM]: "adform",
  [ResourceChannel.MATOMO]: "matomo",
  [ResourceChannel.GOOGLE_SHEETS]: "google_sheets",
  [ResourceChannel.OMNIUM]: "omnium",
  [ResourceChannel.SHOPIFY]: "shopify",
} as const satisfies Record<ResourceChannel, string>;

function getResourceAccounts({
  queryKey: [, { channel }],
}: QueryFunctionContext<{ channel: ResourceChannel }>): Promise<
  ResourceAccount[]
> {
  const path = `/external_apis/${circumventAdBlocker(
    channelToPath[channel],
  )}_resources/`;
  return api.get(path).then((response) => response.data);
}

export function useResourceAccountsQuery({
  channel,
}: {
  channel: Ref<ResourceChannel>;
}) {
  const queryKey = ["resourceAccounts", { channel }] as const;
  return reactive(useQuery({ queryKey, queryFn: getResourceAccounts }));
}

export interface ResourceGroup {
  pk: number;
  name: string;
  createdAt: string;
  modifiedAt: string;
  user: number;
  resources: number[];
}
interface CreateResourceGroupPayload {
  name: string;
  resources: number[];
}
export type UpdateResourceGroupPayload = Pick<
  ResourceGroup,
  "pk" | "name" | "resources"
>;
function createResourceGroup(
  payload: CreateResourceGroupPayload,
): Promise<ResourceGroup> {
  return api
    .post("/resources/resource_groups/", payload)
    .then((response) => response.data);
}
function updateResourceGroup(
  resourceGroup: UpdateResourceGroupPayload,
): Promise<ResourceGroup> {
  return api
    .put(`/resources/resource_groups/${resourceGroup.pk}/`, resourceGroup)
    .then((res) => res.data);
}

function listResourceGroups(): Promise<ResourceGroup[]> {
  return api
    .get("/resources/resource_groups/")
    .then((response) => response.data);
}
function deleteResourceGroup(resourceGroupId: number): Promise<void> {
  return api.delete(`/resources/resource_groups/${resourceGroupId}/`);
}
export function useResourceGroupsQuery() {
  return reactive(
    useQuery({ queryKey: ["resourceGroups"], queryFn: listResourceGroups }),
  );
}

export function useCreateResourceGroupMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: createResourceGroup,
      onError: () => gtmResourceGroup.saveCreate("Error"),
      onSuccess: (newResourceGroup) => {
        gtmResourceGroup.saveCreate("Success");
        queryClient.setQueryData<ResourceGroup[]>(
          ["resourceGroups"],
          (resourceGroups) => [...(resourceGroups ?? []), newResourceGroup],
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["resourceGroups"] });
      },
    }),
  );
}

export function useUpdateResourceGroupMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: updateResourceGroup,
      onError: () => gtmResourceGroup.saveEdit("Error"),
      onSuccess: (newResourceGroup) => {
        gtmResourceGroup.saveEdit("Success");
        queryClient.setQueryData<ResourceGroup[]>(
          ["resourceGroups"],
          (resourceGroups) =>
            resourceGroups?.map((resourceGroup) =>
              resourceGroup.pk === newResourceGroup.pk
                ? newResourceGroup
                : resourceGroup,
            ),
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["resourceGroups"] });
      },
    }),
  );
}

export function useDeleteResourceGroupMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: deleteResourceGroup,
      onError: () => gtmResourceGroup.saveDelete("Error"),
      onSuccess: (_, pk) => {
        gtmResourceGroup.saveDelete("Success");
        queryClient.setQueriesData<ResourceGroup[]>(
          { queryKey: ["resourceGroups"] },
          (resourceGroups) =>
            resourceGroups?.filter(
              (resourceGroup: ResourceGroup) => resourceGroup.pk !== pk,
            ),
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["resourceGroups"] });
      },
    }),
  );
}

export interface ResourceAccessWarning {
  warning?: string;
  details?: string[];
}

// Credential channels (Xandr/C·ALT/Kobler/Matomo/Omnium) return the partial-access
// warning in a 200 body instead of the redirect URL the OAuth channels use, so the
// connect step has to fold it back into the select-channel query itself.
export function missingResourceAccessQuery(
  data: ResourceAccessWarning,
): Record<string, string> {
  return data?.warning
    ? { warning: data.warning, details: JSON.stringify(data.details ?? []) }
    : {};
}

function authorizeXandr({
  username,
  password,
  tokenId,
}: {
  username: string;
  password: string;
  tokenId?: number;
}): Promise<ResourceAccessWarning> {
  return api
    .post(
      "/oauth/xandr/authorize/",
      { username, password },
      { params: { token_id: tokenId } },
    )
    .then((response) => response.data);
}

export function useAuthorizeXandrMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: authorizeXandr,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["resources"] });
        queryClient.invalidateQueries({ queryKey: ["tokens"] });
      },
    }),
  );
}

function authorizeCAlt({
  token,
  tokenId,
}: {
  token: string;
  tokenId?: number;
}): Promise<ResourceAccessWarning> {
  return api
    .post(
      "/oauth/c_alt/authorize/",
      { token },
      { params: { token_id: tokenId } },
    )
    .then((response) => response.data);
}

export function useAuthorizeCAltMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: authorizeCAlt,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["resources"] });
        queryClient.invalidateQueries({ queryKey: ["tokens"] });
      },
    }),
  );
}

function authorizeKobler({
  token,
  tokenId,
}: {
  token: string;
  tokenId?: number;
}): Promise<ResourceAccessWarning> {
  return api
    .post(
      "/oauth/kobler/authorize/",
      { token },
      { params: { token_id: tokenId } },
    )
    .then((response) => response.data);
}

export function useAuthorizeKoblerMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: authorizeKobler,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["resources"] });
        queryClient.invalidateQueries({ queryKey: ["tokens"] });
      },
    }),
  );
}

function authorizeMatomo({
  token,
  serverUrl,
  tokenId,
}: {
  token: string;
  serverUrl: string;
  tokenId?: number;
}): Promise<ResourceAccessWarning> {
  return api
    .post(
      "/oauth/matomo/authorize/",
      { token, server_url: serverUrl },
      { params: { token_id: tokenId } },
    )
    .then((response) => response.data);
}

export function useAuthorizeMatomoMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: authorizeMatomo,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["resources"] });
        queryClient.invalidateQueries({ queryKey: ["tokens"] });
      },
    }),
  );
}

function authorizeOmnium({
  clientId,
  clientSecret,
  apiBaseUrl,
  tokenId,
}: {
  clientId: string;
  clientSecret: string;
  apiBaseUrl?: string;
  tokenId?: number;
}): Promise<ResourceAccessWarning> {
  return api
    .post(
      "/oauth/omnium/authorize/",
      {
        client_id: clientId,
        client_secret: clientSecret,
        api_base_url: apiBaseUrl,
      },
      { params: { token_id: tokenId } },
    )
    .then((response) => response.data);
}

export function useAuthorizeOmniumMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: authorizeOmnium,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["resources"] });
        queryClient.invalidateQueries({ queryKey: ["tokens"] });
      },
    }),
  );
}

export function getSocialMediaProfileUrl(
  resource: DeepReadonly<
    | FacebookOrganicResource
    | LinkedInOrganicResource
    | InstagramOrganicResource
    | YoutubeResource
    | TikTokOrganicResource
    | GoogleSheetsResource
  >,
): string | undefined {
  switch (resource.channel) {
    case ResourceChannel.FACEBOOK_ORGANIC:
      return `https://www.facebook.com/${resource.externalId}/`;
    case ResourceChannel.LINKED_IN_ORGANIC:
      return `https://www.linkedin.com/company/${resource.externalId}/`;
    case ResourceChannel.INSTAGRAM_ORGANIC: {
      if ("username" in resource.metadata) {
        return `https://www.instagram.com/${resource.metadata.username}/`;
      } else {
        return undefined;
      }
    }
    case ResourceChannel.YOUTUBE:
      return `https://www.youtube.com/channel/${resource.externalId}`;
    case ResourceChannel.TIK_TOK_ORGANIC:
      return `https://www.tiktok.com/@${resource.externalId}`;
    case ResourceChannel.GOOGLE_SHEETS:
      return undefined;
    default:
      assertUnreachable(resource);
  }
}

function uploadResourceIcon(resourceId: number, file: File): Promise<Resource> {
  const formData = new FormData();
  formData.append("file", file);

  return api
    .post(`/resources/resources/${resourceId}/upload_icon/`, formData, {
      headers: {
        "Content-Type": undefined,
      },
    })
    .then((response) => response.data);
}

export function useUploadResourceIconMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: ({ resourceId, file }: { resourceId: number; file: File }) =>
        uploadResourceIcon(resourceId, file),
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["resources"] });
      },
    }),
  );
}

function deleteResourceIcon(resourceId: number): Promise<Resource> {
  return api
    .delete(`/resources/resources/${resourceId}/delete_icon/`)
    .then((response) => response.data);
}

export function useDeleteResourceIconMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: deleteResourceIcon,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["resources"] });
      },
    }),
  );
}

// Attribution window configuration (Snapchat Ads and Facebook Ads)
export const CHANNELS_WITH_ATTRIBUTION_WINDOWS: ResourceChannel[] = [
  ResourceChannel.SNAPCHAT_ADS,
  ResourceChannel.FACEBOOK_ADS,
];

export interface AttributionWindowOption {
  label: string;
  value: string;
}

// Meta's action_attribution_windows tokens, shown verbatim. A resource is pinned
// to at most one; an empty selection means the unified (per ad set) setting.
// 7d_view / 28d_view are omitted: Meta deprecated them for all API versions on
// 2026-01-12 and no longer returns them.
export const FACEBOOK_ATTRIBUTION_WINDOW_OPTIONS: AttributionWindowOption[] = [
  { label: "1d_click", value: "1d_click" },
  { label: "7d_click", value: "7d_click" },
  { label: "28d_click", value: "28d_click" },
  { label: "1d_view", value: "1d_view" },
];

export const SNAPCHAT_CLICK_THROUGH_OPTIONS: AttributionWindowOption[] = [
  { label: "1 day", value: "1_DAY" },
  { label: "7 days", value: "7_DAY" },
  { label: "28 days", value: "28_DAY" },
];

export const SNAPCHAT_VIEW_THROUGH_OPTIONS: AttributionWindowOption[] = [
  { label: "1 hour", value: "1_HOUR" },
  { label: "3 hours", value: "3_HOUR" },
  { label: "6 hours", value: "6_HOUR" },
  { label: "1 day", value: "1_DAY" },
  { label: "7 days", value: "7_DAY" },
  { label: "28 days", value: "28_DAY" },
];

export const ATTRIBUTION_WINDOW_DEFAULTS: Partial<
  Record<ResourceChannel, { clickThrough: string; viewThrough: string }>
> = {
  [ResourceChannel.SNAPCHAT_ADS]: {
    clickThrough: "28_DAY",
    viewThrough: "1_DAY",
  },
};

export const ATTRIBUTION_WINDOW_OPTIONS: Partial<
  Record<
    ResourceChannel,
    {
      clickThrough: AttributionWindowOption[];
      viewThrough: AttributionWindowOption[];
    }
  >
> = {
  [ResourceChannel.SNAPCHAT_ADS]: {
    clickThrough: SNAPCHAT_CLICK_THROUGH_OPTIONS,
    viewThrough: SNAPCHAT_VIEW_THROUGH_OPTIONS,
  },
};

export function getAttributionWindowLabel(
  value: string,
  options: AttributionWindowOption[],
): string {
  return options.find((o) => o.value === value)?.label ?? value;
}

function updateAttributionWindows({
  resourceId,
  clickThrough,
  viewThrough,
  actionAttributionWindows,
}: {
  resourceId: number;
  clickThrough?: string;
  viewThrough?: string;
  actionAttributionWindows?: string[];
}): Promise<Resource> {
  // Facebook uses a single flat window list; Snapchat uses click/view scalars.
  const payload =
    actionAttributionWindows !== undefined
      ? { actionAttributionWindows }
      : { clickThrough, viewThrough };
  return api
    .patch(`/resources/resources/${resourceId}/attribution_windows/`, payload)
    .then((response) => response.data);
}

export function useUpdateAttributionWindowsMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: updateAttributionWindows,
      onSuccess: (updatedResource) => {
        queryClient.setQueriesData<Resource[]>(
          { queryKey: ["resources"] },
          (resources) =>
            resources?.map((r) =>
              r.pk === updatedResource.pk ? updatedResource : r,
            ),
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["resources"] });
      },
    }),
  );
}

/**
 * Helper function to find a resource by ID from a resource list
 */
export function getResourceFromId(
  resources: Resource[] | undefined,
  id: number | undefined,
): Resource | undefined {
  if (!id || !resources) return undefined;
  return resources.find((resource) => resource.pk === id);
}

export interface OmniumBrand {
  id: string;
  name: string;
  storeCount: number;
  selected?: boolean;
  // False for a saved brand discovery no longer lists (all its stores inactive).
  available?: boolean;
}

function listOmniumBrands(): Promise<OmniumBrand[]> {
  return api
    .get("/external_apis/omnium_brands/")
    .then((response) => response.data);
}

// Brands visible to the connecting user's own Omnium token (connect step).
export function useOmniumBrandsQuery(
  enabled: MaybeRefOrGetter<boolean> = true,
) {
  return reactive(
    useQuery({
      queryKey: ["omnium-brands"],
      queryFn: listOmniumBrands,
      enabled: computed(() => toValue(enabled)),
    }),
  );
}

function listResourceOmniumBrands(resourceId: number): Promise<OmniumBrand[]> {
  return api
    .get(`/resources/resources/${resourceId}/omnium-brands/`)
    .then((response) => response.data);
}

// Brands for an existing account via its own token, so non-owners can edit too.
export function useResourceOmniumBrandsQuery(
  resourceId: MaybeRefOrGetter<number | null>,
) {
  return reactive(
    useQuery({
      queryKey: computed(() => [
        "omnium-brands",
        "resource",
        toValue(resourceId),
      ]),
      queryFn: () => listResourceOmniumBrands(toValue(resourceId) as number),
      enabled: computed(() => toValue(resourceId) !== null),
    }),
  );
}

function updateOmniumBrands({
  resourceId,
  brands,
}: {
  resourceId: number;
  brands: string[];
}): Promise<Resource> {
  return api
    .patch(`/resources/resources/${resourceId}/omnium-brands/`, { brands })
    .then((response) => response.data);
}

function muteOmniumCoverage({
  resourceId,
  muted,
}: {
  resourceId: number;
  muted: boolean;
}): Promise<Resource> {
  return api
    .patch(`/resources/resources/${resourceId}/omnium-coverage/`, { muted })
    .then((response) => response.data);
}

/** Silence, or restore, the "Omnium has indexed few orders" warning. */
export function useMuteOmniumCoverageMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: muteOmniumCoverage,
      onSuccess: (updatedResource) => {
        queryClient.setQueriesData<Resource[]>(
          { queryKey: ["resources"] },
          (resources) =>
            resources?.map((r) =>
              r.pk === updatedResource.pk ? updatedResource : r,
            ),
        );
        // The Stores overview carries its own copy of the verdict.
        queryClient.invalidateQueries({ queryKey: ["crmStoresOverview"] });
      },
    }),
  );
}

export function useUpdateOmniumBrandsMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: updateOmniumBrands,
      onSuccess: (updatedResource) => {
        queryClient.setQueriesData<Resource[]>(
          { queryKey: ["resources"] },
          (resources) =>
            resources?.map((r) =>
              r.pk === updatedResource.pk ? updatedResource : r,
            ),
        );
        queryClient.invalidateQueries({
          queryKey: ["omnium-brands", "resource", updatedResource.pk],
        });
      },
    }),
  );
}
